import { Link } from "react-router-dom";

import type { ProductListItem } from "../../api/types";
import {
  FAMILY_LABELS,
  GLASSES_TYPE_LABELS,
  familyForCategory,
  glassesTypeForCategory,
} from "../../catalog/taxonomy";
import { StatusBadge, formatDateTime } from "../../components/ui";

/**
 * One page of products, and the two actions an administrator takes on one.
 *
 * Every column is a field the listing endpoint actually returns. Nothing is
 * derived that the backend did not send, and no internal identifier is printed:
 * the product id appears only inside the hrefs of the committed routes, which
 * is where it already lives for every other screen in this application.
 */

/**
 * Whether Archive is worth offering on this row.
 *
 * PublishService.archive has exactly one precondition — the product must not
 * already be archived, which it answers with the "already_archived" conflict.
 * This mirrors that single rule and nothing more. Every other reason an archive
 * might fail stays the server's to decide on the confirmation screen, so this
 * list cannot end up offering a button that is guaranteed to be refused, nor
 * hiding one the backend would have accepted.
 */
function canArchive(item: ProductListItem): boolean {
  return item.status !== "archived";
}

/**
 * A product that has not finished becoming one.
 *
 * Its photographs are still being prepared, or they were prepared and want a
 * look. Either way the next thing to do is not "edit the details" — it is to
 * pick the import back up where it stopped, which is what Resume means.
 */
function isMidImport(item: ProductListItem): boolean {
  return item.status === "processing" || item.status === "needs_review";
}

/**
 * A draft sitting alongside a revision customers can already see.
 *
 * That is the one shape in which "Publish Changes" is the right words — a first
 * publish is Publish, and a product with no draft has nothing to publish.
 */
function hasUnpublishedChanges(item: ProductListItem): boolean {
  return item.draft_revision_number !== null && item.active_revision_number !== null;
}

/**
 * Why lifecycle actions here are links and never calls.
 *
 * The listing endpoint does not send available_actions — only the detail
 * endpoint does — so this row cannot know with authority whether a transition
 * is permitted. Rather than re-derive the publishing rules in the browser and
 * risk offering a button guaranteed to be refused, each lifecycle action opens
 * the workspace with ?action=… and LifecycleActions decides: it raises its own
 * confirmation only when the backend's available_actions actually contains it,
 * and otherwise simply shows the product. A wrong guess here degrades to "the
 * product opened", never to a failed lifecycle call.
 */
function workspaceAction(item: ProductListItem, action: string): string {
  return `/products/${item.product_id}?action=${action}`;
}

export function ProductTable({ items }: { items: ProductListItem[] }) {
  return (
    <div className="productList__scroll">
      <table className="table">
        <thead>
          <tr>
            <th scope="col" className="productList__thumbCol">
              <span className="sr-only">Photograph</span>
            </th>
            <th scope="col">Product</th>
            <th scope="col">Slug</th>
            <th scope="col">Type</th>
            <th scope="col">Status</th>
            <th scope="col">Updated</th>
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <ProductRow key={item.product_id} item={item} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProductRow({ item }: { item: ProductListItem }) {
  const family = familyForCategory(item.category);
  const glassesType = glassesTypeForCategory(item.category);

  return (
    <tr>
      <td className="productList__thumbCol">
        {item.listing_card_url ? (
          <img
            className="productList__thumb"
            src={item.listing_card_url}
            alt=""
            loading="lazy"
          />
        ) : (
          <span className="productList__thumb productList__thumb--empty" aria-hidden="true" />
        )}
      </td>

      <td>
        {/* The name is the way into the product's full workspace. It is a real
            link rather than a click handler on the row, so Edit and Archive can
            sit in the same row without the row swallowing the click. */}
        <Link className="productList__name" to={`/products/${item.product_id}`}>
          {item.name}
        </Link>
        {/* The shop's own code, never the slug. Products created before the
            field existed genuinely have none and say so. */}
        <span
          className={
            item.product_code
              ? "productList__code"
              : "productList__code productList__code--missing"
          }
        >
          {item.product_code ?? "Code missing"}
        </span>
      </td>

      <td className="mono">{item.slug}</td>

      <td>
        <span className="productList__family">
          {family ? FAMILY_LABELS[family] : "Not classified"}
        </span>
        {glassesType ? (
          <span className="productList__familyDetail">
            {GLASSES_TYPE_LABELS[glassesType]}
          </span>
        ) : null}
      </td>

      <td>
        <StatusBadge status={item.status} />
      </td>

      <td className="muted">{formatDateTime(item.updated_at)}</td>

      <td>
        <div className="productList__actions">
          {/* Text labels routed into the one workspace. Nothing acts from this
              row: every lifecycle call belongs to LifecycleActions, which is
              reached with the action already selected. */}
          <Link className="btn btn--sm" to={`/products/${item.product_id}`}>
            Open
          </Link>

          {/* Resume and Edit are the same destination in different words, and
              they are mutually exclusive on purpose: a product still being
              processed is picked back up, not edited. */}
          {isMidImport(item) ? (
            <Link
              className="btn btn--sm"
              to={workspaceAction(item, "open_draft")}
              data-testid={`resume-${item.product_id}`}
            >
              Resume
            </Link>
          ) : (
            <Link className="btn btn--sm" to={`/products/${item.product_id}/edit`}>
              Edit
            </Link>
          )}

          {hasUnpublishedChanges(item) ? (
            <Link
              className="btn btn--sm btn--primary"
              to={workspaceAction(item, "publish_changes")}
              data-testid={`publish-changes-${item.product_id}`}
            >
              Publish Changes
            </Link>
          ) : null}

          {canArchive(item) ? (
            <Link
              className="btn btn--sm btn--danger"
              to={workspaceAction(item, "archive")}
              data-testid={`archive-${item.product_id}`}
            >
              Archive
            </Link>
          ) : (
            <span className="productList__ineligible">Already archived</span>
          )}
        </div>
      </td>
    </tr>
  );
}
